import React from 'react';
import {
  Mail,
  Users,
  Settings,
  BarChart3,
  ArrowRight,
  ShieldCheck,
  Zap,
  LogOut
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { signOut } from "firebase/auth";
import auth from '../../../../firebase.init';


const EmailMerketingDash = () => {
  const navigate = useNavigate();

  const handleSignout = () => {
    signOut(auth)
      .then(() => {
        navigate("/login");
      })
      .catch((error) => {
        console.error("Logout error:", error);
      });
  };

  const cards = [
    {
      title: 'Email Analytics',
      desc: 'Track Total Sent, Daily Sent, Weekly Sent and campaign performance in real-time.',
      icon: <BarChart3 size={32} />,
      color: 'primary',
      link: '/admin/email-analytics',
      highlight: true
    },
    {
      title: 'Email Templates',
      desc: 'Design and manage custom AI-powered email templates.',
      icon: <Mail size={32} />,
      color: 'info',
      link: '/admin/email-templates'
    },
    {
      title: 'Audience Manager',
      desc: 'Add, segment, and manage your marketing contacts.',
      icon: <Users size={32} />,
      color: 'success',
      link: '/admin/user-email-marketing'
    },
    {
      title: 'Setting Configuration',
      desc: 'Configure your email delivery settings and Gemini API Keys.',
      icon: <Settings size={32} />,
      color: 'danger',
      link: '/admin/user-marketing-smpt'
    }
  ];

  return (
    <div className="marketing-hub-container py-5 px-3">
      <div className="container">
        <div className="text-center mb-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="position-relative"
          >
            <div className="position-absolute top-0 end-0">
              <button 
                onClick={handleSignout}
                className="btn btn-outline-danger rounded-pill d-flex align-items-center gap-2 px-3 py-2 transition-all shadow-sm"
                style={{ background: 'rgba(220, 53, 69, 0.05)', border: '1px solid rgba(220, 53, 69, 0.2)' }}
              >
                <LogOut size={18} />
                <span className="fw-semibold">Logout</span>
              </button>
            </div>
            <span className="badge bg-primary-soft text-primary px-3 py-2 rounded-pill mb-3">
              Email Portal
            </span>
            <h1 className="text-white fw-bold display-5 mb-3">Email Marketing Dashboard</h1>
            <p className="text-muted mx-auto" style={{ maxWidth: '600px' }}>
              Scale your outreach with AI-generated content and precision tracking. 
              Manage everything from delivery settings to performance analytics.
            </p>
          </motion.div>
        </div>

        <div className="row g-4">
          {cards.map((card, index) => (
            <div key={index} className={card.highlight ? "col-md-12" : "col-md-4"}>
              <motion.div 
                className={`hub-card glass-card p-4 ${card.highlight ? 'highlight-border' : ''}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="d-flex align-items-center justify-content-between mb-4">
                  <div className={`icon-wrapper bg-${card.color}-soft text-${card.color}`}>
                    {card.icon}
                  </div>
                  {card.highlight && (
                    <span className="badge bg-warning-soft text-warning d-flex align-items-center gap-1">
                      <ShieldCheck size={14} /> Performance Ready
                    </span>
                  )}
                </div>
                
                <div className={card.highlight ? "row align-items-center" : ""}>
                  <div className={card.highlight ? "col-md-8" : ""}>
                    <h3 className="text-white fw-bold mb-2">{card.title}</h3>
                    <p className="text-muted mb-4">{card.desc}</p>
                  </div>
                  <div className={card.highlight ? "col-md-4 text-md-end" : ""}>
                    <Link to={card.link} className={`btn btn-${card.color} rounded-pill px-4 py-2 d-inline-flex align-items-center gap-2`}>
                      {card.highlight ? 'Open Analytics' : 'Manage'}
                      <ArrowRight size={18} />
                    </Link>
                  </div>
                </div>
              </motion.div>
            </div>
          ))}
        </div>
      </div>

      <style jsx="true">{`
        .marketing-hub-container {
          background: radial-gradient(circle at top right, #1e293b, #0f172a);
          min-height: 90vh;
        }
        .glass-card {
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 28px;
          transition: all 0.3s ease;
        }
        .glass-card:hover {
          background: rgba(255, 255, 255, 0.05);
          border-color: rgba(255, 255, 255, 0.2);
        }
        .highlight-border {
          border: 1px solid rgba(13, 110, 253, 0.3);
          background: linear-gradient(135deg, rgba(13, 110, 253, 0.05) 0%, rgba(255, 255, 255, 0.03) 100%);
        }
        .icon-wrapper {
          width: 64px;
          height: 64px;
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .bg-primary-soft { background: rgba(13, 110, 253, 0.15); }
        .bg-info-soft { background: rgba(13, 202, 240, 0.15); }
        .bg-success-soft { background: rgba(25, 135, 84, 0.15); }
        .bg-danger-soft { background: rgba(220, 53, 69, 0.15); }
        .bg-warning-soft { background: rgba(255, 193, 7, 0.15); }
        
        .btn-primary { background: #0d6efd; border: none; box-shadow: 0 4px 14px rgba(13, 110, 253, 0.4); }
        .btn-info { background: #0dcaf0; border: none; color: #000; box-shadow: 0 4px 14px rgba(13, 202, 240, 0.4); }
        .btn-success { background: #198754; border: none; box-shadow: 0 4px 14px rgba(25, 135, 84, 0.4); }
        .btn-danger { background: #dc3545; border: none; box-shadow: 0 4px 14px rgba(220, 53, 69, 0.4); }
        
        .transition-all { transition: all 0.3s ease; }
      `}</style>
    </div>
  );
};

export default EmailMerketingDash;
